import { useState } from 'react';

export interface RetroactiveStartData {
  departureTime: Date;
  // #3019 — the ACTUAL time the rep arrived at the store. Closes the drive
  // segment. Previously the drive end was assumed to be "now" (the moment of the
  // backfill), so a rep who reached the store early but only started the app late
  // got a hugely inflated drive time (diag #67: 134 min for a 15 min drive).
  arrivalTime: Date;
  startOdometer: number | null;
  arrivalOdometer: number | null;
}

interface RetroactiveStartDayProps {
  storeName: string;
  onSubmit: (data: RetroactiveStartData) => void;
  onCancel: () => void;
  onSkip: () => void;
  isLoading?: boolean;
}

export default function RetroactiveStartDay({
  storeName,
  onSubmit,
  onCancel,
  onSkip,
  isLoading = false,
}: RetroactiveStartDayProps) {
  // Default departure time to 1 hour ago (rounded to nearest 5 min)
  const getDefaultDepartureTime = () => {
    const now = new Date();
    now.setHours(now.getHours() - 1);
    now.setMinutes(Math.round(now.getMinutes() / 5) * 5);
    now.setSeconds(0);
    return now;
  };

  const [departureTime, setDepartureTime] = useState<Date>(getDefaultDepartureTime());
  // #3019 — actual arrival time at the store; defaults to now (the common case:
  // the rep is filling this in right as they arrive), but editable for the
  // late-check-in case that inflated the drive.
  const [arrivalTime, setArrivalTime] = useState<Date>(new Date());
  const [startOdometer, setStartOdometer] = useState<string>('');
  const [arrivalOdometer, setArrivalOdometer] = useState<string>('');
  // skipMileage is controlled by the "Skip mileage tracking" button which calls handleSkipMileage
  const [skipMileage] = useState(false);

  // Format time for input (HH:MM)
  const formatTimeForInput = (date: Date) => {
    return date.toTimeString().slice(0, 5);
  };

  // Parse time input to Date
  const parseTimeInput = (timeStr: string): Date => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseTimeInput(e.target.value);
    setDepartureTime(newTime);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Arrival odometer is REQUIRED on the mileage-tracking path
    // (#1893 / Rob @ Publix 886 — used to be optional and an unfilled
    // value let the system create CallLog rows with bogus 0->1 drive
    // segments). The starting odometer is allowed to be 0 — that's a
    // valid "I don't track home odometer" placeholder.
    const startOdoRaw = startOdometer.trim();
    const arrivalOdoRaw = arrivalOdometer.trim();
    if (arrivalOdoRaw === '') return; // arrival required
    const arrivalOdo = parseFloat(arrivalOdoRaw);
    if (!Number.isFinite(arrivalOdo)) return;
    const startOdo = startOdoRaw === '' ? null : parseFloat(startOdoRaw);
    if (startOdo !== null && !Number.isFinite(startOdo)) return;
    if (startOdo !== null && arrivalOdo < startOdo) return; // arrival must be >= start

    // Departure time must be in the past — guard against typos that
    // would make the drive segment go backward.
    if (departureTime.getTime() > Date.now()) return;
    // #3019 — arrival must be after departure and not in the future.
    if (arrivalTime.getTime() < departureTime.getTime() || arrivalTime.getTime() > Date.now()) return;

    onSubmit({
      departureTime,
      arrivalTime,
      startOdometer: skipMileage ? null : (startOdo ?? 0),
      arrivalOdometer: skipMileage ? null : arrivalOdo,
    });
  };

  const handleSkipMileage = () => {
    if (arrivalTime.getTime() < departureTime.getTime() || arrivalTime.getTime() > Date.now()) return;
    onSubmit({
      departureTime,
      arrivalTime,
      startOdometer: null,
      arrivalOdometer: null,
    });
  };

  const calculateDriveMiles = (): number | null => {
    const start = parseFloat(startOdometer);
    const arrival = parseFloat(arrivalOdometer);
    if (!isNaN(start) && !isNaN(arrival) && arrival >= start) {
      return arrival - start;
    }
    return null;
  };

  const driveMiles = calculateDriveMiles();
  const arrivalOdometerNum = parseFloat(arrivalOdometer);
  const startOdometerNum = parseFloat(startOdometer);
  const arrivalMissing = arrivalOdometer.trim() === '';
  const isArrivalInvalid =
    !arrivalMissing && !isNaN(arrivalOdometerNum) && !isNaN(startOdometerNum) && arrivalOdometerNum < startOdometerNum;
  const departureInFuture = departureTime.getTime() > Date.now();
  // #3019 — arrival-time validity (must be after departure, not in the future).
  const arrivalBeforeDeparture = arrivalTime.getTime() < departureTime.getTime();
  const arrivalTimeInFuture = arrivalTime.getTime() > Date.now();
  const arrivalTimeInvalid = arrivalBeforeDeparture || arrivalTimeInFuture;
  const driveMinutes = Math.max(0, Math.round((arrivalTime.getTime() - departureTime.getTime()) / 60000));
  // Submit only allowed when arrival is filled (on mileage path) and
  // odometer ordering / departure + arrival times validate.
  const canSubmit =
    !arrivalTimeInvalid && (skipMileage || (!arrivalMissing && !isArrivalInvalid && !departureInFuture));

  return (
    <div className="rounded-xl bg-white p-4 shadow-card">
      {/* Header */}
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
          <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-slate-800">Clock In Retroactively</h3>
          <p className="mt-1 text-sm text-slate-500">
            You're at <span className="font-medium">{storeName}</span>. Let's capture your drive here.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Departure Time */}
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            What time did you leave home?
          </label>
          <input
            type="time"
            value={formatTimeForInput(departureTime)}
            onChange={handleTimeChange}
            disabled={isLoading}
            className={`w-full rounded-lg border px-4 py-3 text-lg text-slate-900 focus:outline-none focus:ring-2 disabled:bg-slate-50 ${
              departureInFuture
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500/20'
            }`}
          />
          {departureInFuture ? (
            <p className="mt-1 text-xs text-red-500">
              Time must be in the past — you can't have left home in the future.
            </p>
          ) : (
            <p className="mt-1 text-xs text-slate-500">
              This will be your day start time
            </p>
          )}
        </div>

        {/* #3019 — Arrival Time. Defaults to now, but the rep can correct it when
            they got to the store earlier than they started the app, so the drive
            segment isn't inflated by the gap (diag #67). */}
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            What time did you get to the store?
          </label>
          <input
            type="time"
            value={formatTimeForInput(arrivalTime)}
            onChange={(e) => setArrivalTime(parseTimeInput(e.target.value))}
            disabled={isLoading}
            className={`w-full rounded-lg border px-4 py-3 text-lg text-slate-900 focus:outline-none focus:ring-2 disabled:bg-slate-50 ${
              arrivalTimeInvalid
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500/20'
            }`}
          />
          {arrivalTimeInvalid ? (
            <p className="mt-1 text-xs text-red-500">
              {arrivalBeforeDeparture
                ? "Arrival can't be before you left home."
                : "Arrival can't be in the future."}
            </p>
          ) : (
            <p className="mt-1 text-xs text-slate-500">
              Closes your drive segment — drive time so far: {driveMinutes} min
            </p>
          )}
        </div>

        {!skipMileage && (
          <>
            {/* Starting Odometer (optional — 0 is valid for "didn't track from home") */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Odometer when you left home
                <span className="ml-1 text-slate-400">(optional · leave blank or 0 if untracked)</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={startOdometer}
                  onChange={(e) => setStartOdometer(e.target.value)}
                  placeholder="e.g., 45678.9"
                  disabled={isLoading}
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 pr-12 font-mono text-lg text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:bg-slate-50"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">mi</span>
              </div>
            </div>

            {/* Arrival Odometer — REQUIRED (#1893). The on-site reading
                is what closes the drive-to-store segment; if it's blank
                downstream code writes bogus 0/1 records. */}
            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Odometer right now at the store
                <span className="ml-1 text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  value={arrivalOdometer}
                  onChange={(e) => setArrivalOdometer(e.target.value)}
                  placeholder="Read it off the dash now"
                  disabled={isLoading}
                  required
                  className={`w-full rounded-lg border px-4 py-3 pr-12 font-mono text-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 disabled:bg-slate-50 ${
                    isArrivalInvalid
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
                      : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500/20'
                  }`}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-400">mi</span>
              </div>
              {isArrivalInvalid && (
                <p className="mt-1 text-xs text-red-500">
                  Must be greater than or equal to the starting odometer
                </p>
              )}
              {arrivalMissing && (
                <p className="mt-1 text-xs text-slate-500">
                  Required — this is what closes the drive segment.
                </p>
              )}
            </div>

            {/* Drive Miles Summary */}
            {driveMiles !== null && (
              <div className="mb-4 rounded-lg bg-green-50 p-3">
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-green-800">
                    Drive to store: {driveMiles.toFixed(1)} miles
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-3 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || !canSubmit}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-3 font-semibold text-white hover:bg-primary-700 disabled:bg-slate-300"
          >
            {isLoading ? (
              <>
                <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Clocking in...</span>
              </>
            ) : (
              <span>Clock In</span>
            )}
          </button>
        </div>

        {/* Skip Mileage Option */}
        {!skipMileage && (
          <button
            type="button"
            onClick={handleSkipMileage}
            disabled={isLoading}
            className="mt-3 w-full text-center text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
          >
            Skip mileage tracking
          </button>
        )}

        {/* Full Skip Option */}
        <button
          type="button"
          onClick={onSkip}
          disabled={isLoading}
          className="mt-2 w-full text-center text-xs text-slate-400 hover:text-slate-600 disabled:opacity-50"
        >
          Clock in with current time instead
        </button>
      </form>
    </div>
  );
}
